import { buildBusyworkSectionProps } from "@/puck/defaults/busyworkSectionDefaults";
import { buildFilledWhileYouWatchSectionProps } from "@/puck/defaults/filledWhileYouWatchSectionDefaults";
import { buildUploadExpertsSectionProps } from "@/puck/defaults/uploadExpertsSectionDefaults";
import type { LandingPuckData } from "@/puck/types";
import { normalizeChatMockupBlocksInTree } from "@/utils/chatMockup";

export type StudioTemplateBlock = LandingPuckData["content"][number];

export type StudioTemplate = {
  id: string;
  /** Registered Puck component type to drag from the Templates drawer. */
  componentType: string;
  name: string;
  description: string;
};

type PuckBlock = {
  type: string;
  props: Record<string, unknown>;
};

function isPuckBlock(value: unknown): value is PuckBlock {
  return (
    typeof value === "object" &&
    value !== null &&
    "type" in value &&
    typeof (value as PuckBlock).type === "string" &&
    "props" in value &&
    typeof (value as PuckBlock).props === "object" &&
    (value as PuckBlock).props !== null
  );
}

function remapPuckValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => {
      if (isPuckBlock(item)) {
        return remapPuckTree(item);
      }

      if (
        item &&
        typeof item === "object" &&
        !("type" in item) &&
        !("id" in item && typeof (item as { id?: unknown }).id === "string")
      ) {
        return {
          ...(item as Record<string, unknown>),
          id: crypto.randomUUID(),
        };
      }

      return remapPuckValue(item);
    });
  }

  return value;
}

type RegeneratePuckBlockIdsOptions = {
  /** Keep the root block id — required for Puck insert resolve to finish. */
  preserveRootId?: boolean;
};

function remapPuckTree<Block extends PuckBlock>(
  block: Block,
  options: RegeneratePuckBlockIdsOptions = {},
): Block {
  const rootId =
    options.preserveRootId && typeof block.props.id === "string"
      ? block.props.id
      : crypto.randomUUID();

  const props: Record<string, unknown> = {
    ...block.props,
    id: rootId,
  };

  for (const [key, value] of Object.entries(props)) {
    if (key === "id") continue;
    props[key] = remapPuckValue(value);
  }

  return { ...block, props };
}

/** Deep-clones a Puck block tree and assigns fresh ids to every nested component. */
export function regeneratePuckBlockIds<Block extends PuckBlock>(
  block: Block,
  options: RegeneratePuckBlockIdsOptions = {},
): Block {
  return normalizeChatMockupBlocksInTree(
    remapPuckTree(structuredClone(block), options),
  );
}

export function buildTemplatePreviewBlock(
  template: StudioTemplate,
  rootId: string,
): StudioTemplateBlock | null {
  if (template.componentType === "TemplateBusywork") {
    return {
      type: "TemplateBusywork",
      props: buildBusyworkSectionProps(rootId),
    } as StudioTemplateBlock;
  }

  if (template.componentType === "TemplateUploadExperts") {
    return {
      type: "TemplateUploadExperts",
      props: buildUploadExpertsSectionProps(rootId),
    } as StudioTemplateBlock;
  }

  if (template.componentType === "TemplateFilledWhileYouWatch") {
    return {
      type: "TemplateFilledWhileYouWatch",
      props: buildFilledWhileYouWatchSectionProps(rootId),
    } as StudioTemplateBlock;
  }

  return null;
}

export const STUDIO_LANDING_TEMPLATES: StudioTemplate[] = [
  {
    id: "busywork-section",
    componentType: "TemplateBusywork",
    name: "Busywork section",
    description:
      "Centered header with three feature cards (ask, citations, trends) and nested chat/chart mockups.",
  },
  {
    id: "upload-experts-section",
    componentType: "TemplateUploadExperts",
    name: "Build your own AI experts",
    description:
      "Numbered upload steps beside a Gail Studio mockup with a floating indexed-document badge.",
  },
  {
    id: "filled-while-you-watch-section",
    componentType: "TemplateFilledWhileYouWatch",
    name: "Filled while you watch",
    description:
      "Section heading beside a phone video demo with ACORD status pill and floating COI badge.",
  },
];

export function getStudioLandingTemplates(): StudioTemplate[] {
  return STUDIO_LANDING_TEMPLATES;
}
